<template>
  <div
    class="calendar"
    @click.stop
  >
    <label class="label">
      <span class="is-semi-bold font-sm">
        <slot />
      </span>
    </label>
    <v-datepicker
      v-model="range"
      placeholder="Selecione uma data"
      :lang="lang"
      :append-to-body="detached"
      :clearable="true"
      :editable="true"
      :range="isRange"
      :disabled="disabled"
      :disabled-date="disabledDate"
      :type="type"
      :value-type="valueType"
      :title-format="titleFormat"
      time-title-format="DD MMM YYYY"
      :format="format"
      range-separator=" até "
      @input="$emit('input', range)"
      @change="$emit('change')"
      @click.stop
    />
  </div>
</template>

<script>
import VDatepicker from 'vue2-datepicker';

import 'vue2-datepicker/index.css';

export default {
  name: 'BaseCalendar',
  components: {
    VDatepicker,
  },
  props: {
    /**
     * Solta o popover no `body`. Inline ele exige overflow visível no
     * ancestral, e aí a caixa que o contém não clipa mais a própria altura.
     */
    detached: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Array],
      default: () => [],
      required: false,
    },
    limitDate: {
      type: Boolean,
      default: true,
      required: false,
    },
    customDisabledDate: {
      type: Function,
      default: null,
      required: false,
    },
    isRange: {
      type: Boolean,
      default: true,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    type: {
      type: String,
      default: 'date',
      required: false,
    },
    valueType: {
      type: String,
      default: 'DD/MM/YYYY',
      required: false,
    },
    format: {
      type: String,
      default: 'DD/MM/YYYY',
      required: false,
    },
    titleFormat: {
      type: String,
      default: 'DD/MM/YY',
      required: false,
    },
  },
  data: () => ({
    range: [],
    lang: {
      formatLocale: {
        months: [
          'Janeiro',
          'Fevereiro',
          'Março',
          'Abril',
          'Maio',
          'Junho',
          'Julho',
          'Agosto',
          'Setembro',
          'Outubro',
          'Novembro',
          'Dezembro',
        ],
        monthsShort: [
          'Jan',
          'Fev',
          'Mar',
          'Abr',
          'Mai',
          'Jun',
          'Jul',
          'Ago',
          'Set',
          'Out',
          'Nov',
          'Dez',
        ],
      },
      days: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
    },
  }),
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.range = value;
      },
    },
  },
  methods: {
    disabledDate(date) {
      if (this.customDisabledDate) return this.customDisabledDate(date);
      if (this.limitDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date > today.getTime();
      }

      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_fieldSignature.scss';

.calendar {
  position: relative;

  .mx-datepicker-range {
    width: 100%;
  }

  .label {
    margin-bottom: 0;

    span {
      font: {
        family: Inter;
        size: 14px;
        weight: 400;
      }
      line-height: 20px;
      color: #666;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  // Só no modo inline: dentro de modal com overflow visível, o cálculo de
  // direção do vue2-datepicker (JS, via getBoundingClientRect) erra e abre o
  // popover pra cima, sobrepondo o conteúdo acima do input. Sempre tem espaço
  // embaixo aqui, então força a direção pra baixo via CSS (!important vence
  // o style inline calculado pela lib). Com `detached` o popover sai para o
  // body, se posiciona sozinho, e este seletor não o alcança.
  :deep(.mx-datepicker-popup) {
    top: 100% !important;
    bottom: auto !important;
  }

  :deep(.mx-input) {
    @include field-frame;
    @include field-text;
    @include field-focus;
    display: block;
    max-width: 100%;
    height: 2.625rem;
    padding: {
      top: calc(0.5em - 1px);
      left: calc(0.75em - 1px);
      bottom: calc(0.5em - 1px);
      right: calc(0.75em - 1px);
    };

    cursor: pointer;
    outline: none;

    &:disabled {
      background: hsl(0, 0%, 96%);
      color: hsl(0, 0%, 48%);
      cursor: not-allowed;
    }
  }
}
</style>
